import { defaultInstance } from './httpClient';
import { searchUsers } from './samApi';

/**
 * Find the SAM DB `user` row for a freshly created Cognito user.
 *
 * `POST /sam/users` returns the Cognito *username* (the email in dev), but the
 * DB stores the Cognito `sub` (a UUID) in `user.cognitoid` — the same key
 * UserDetailDrawer queries by. Querying `/crud/user?cognitoid=<username>`
 * therefore never matches. We resolve the sub first via the SAM users search
 * (admin_get_user, which returns `sub`), then look up by cognitoid=sub, and
 * fall back to the row's email when the sub can't be resolved.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type DbUserRow = { id?: number };

const firstRowId = (resp: unknown): number | null => {
  const body = (resp as { data?: unknown })?.data as { data?: DbUserRow[] } | DbUserRow[] | undefined;
  const rows = Array.isArray(body) ? body : body?.data ?? [];
  return Array.isArray(rows) && rows[0]?.id ? rows[0].id : null;
};

/** The row out of a crud answer, whether it comes as the row, `{ data: row }` or `{ data: [row] }`. */
const rowOf = <T extends DbUserRow>(resp: unknown): T | null => {
  const body = (resp as { data?: unknown })?.data ?? resp;
  const inner = (body as { data?: unknown })?.data ?? body;
  const row = Array.isArray(inner) ? inner[0] : inner;
  return row && typeof row === 'object' && (row as DbUserRow).id ? (row as T) : null;
};

/**
 * SAM-801: the DB `user` row of a listed user. Listings and POST /sam/users carry the numeric `user_id`
 * (backend #646), and confined staff may read another user only by id (backend #629), never by cognitoid.
 * - a number: GET /crud/user/{id};
 * - null: the user has no row in this tenant (or the backend's lookup failed): nothing to read;
 * - undefined: a backend without #646; the legacy cognitoid search, until #646 is in every environment.
 * Errors (a 403 included) reach the caller.
 */
export const fetchDbUser = async <T extends DbUserRow = DbUserRow>(user: { user_id?: number | null; sub?: string }): Promise<T | null> => {
  if (typeof user.user_id === 'number') {
    return rowOf<T>(await defaultInstance({ method: 'GET', url: `/user/${user.user_id}` }));
  }
  if (user.user_id === null || !user.sub) return null;
  return rowOf<T>(await defaultInstance({ method: 'GET', url: '/user', params: { cognitoid: user.sub, limit: 1 } }));
};

const queryDbUser = async (params: Record<string, string>): Promise<number | null> => {
  const resp = await defaultInstance({ method: 'GET', url: '/user', params: { ...params, limit: 1 }, timeout: 6000 });
  return firstRowId(resp);
};

/** Resolve a Cognito username to its `sub`. Returns the input when it already is a sub. */
export const resolveCognitoSub = async (username: string): Promise<string | null> => {
  if (UUID_RE.test(username)) return username;
  try {
    const resp = (await searchUsers(username, 1)) as unknown as {
      users?: Array<{ username?: string; sub?: string }>;
      user?: { sub?: string };
    };
    const users = resp?.users ?? (resp?.user ? [resp.user] : []);
    return users.find((u) => u?.sub)?.sub ?? null;
  } catch {
    return null;
  }
};

export interface FindDbUserOptions {
  /** `user_id` from the POST /sam/users answer (backend #646): used as is when it is a number. */
  knownUserId?: number | null;
  /** Total attempts — the DB row may lag the Cognito create by a few seconds. */
  attempts?: number;
  delayMs?: number;
  /** Test seam. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Returns the DB user id for a Cognito user created via POST /sam/users: the `user_id` it answered with, or
 * else the search below; null when no row turns up after all attempts.
 */
export const findDbUserIdForCreatedUser = async (
  cognitoUsername: string,
  email?: string,
  { knownUserId, attempts = 4, delayMs = 3000, sleep = (ms) => new Promise((r) => setTimeout(r, ms)) }: FindDbUserOptions = {},
): Promise<number | null> => {
  // SAM-801: POST /sam/users answers with the row's id; the search below is only for a null id (no row yet) or a
  // backend without #646.
  if (typeof knownUserId === 'number') return knownUserId;
  const normalizedEmail = (email || (cognitoUsername.includes('@') ? cognitoUsername : '')).trim().toLowerCase();
  let sub: string | null = null;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await sleep(delayMs);
    if (!sub) sub = await resolveCognitoSub(cognitoUsername);
    try {
      if (sub) {
        const id = await queryDbUser({ cognitoid: sub });
        if (id) return id;
      }
      if (normalizedEmail) {
        const id = await queryDbUser({ email: normalizedEmail });
        if (id) return id;
      }
    } catch {
      // timeout or network error — keep retrying
    }
  }
  return null;
};
